// src/hooks/api/useUsers.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest } from '../../util/server_request';
import { notifyUser } from '../../util/util';

// Query Keys
export const usersKeys = {
  all: ['users'],
  lists: () => [...usersKeys.all, 'list'],
  list: (pageNum, pageSize) => [...usersKeys.lists(), { pageNum, pageSize }],
  detail: (id) => [...usersKeys.all, 'detail', id],
};

// Fetch users with pagination
export const useUsers = (pageNum = 0, pageSize = 10) => {
  return useQuery({
    queryKey: usersKeys.list(pageNum, pageSize),
    queryFn: async () => {
      const result = await serverRequest('GET', `users?pageNum=${pageNum}&pageSize=${pageSize}`);
      return {
        content: Array.isArray(result?.data?.content) ? result?.data?.content : [],
        totalElements: result?.data?.totalElements || 0,
      };
    },
    keepPreviousData: true,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Create new user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData) => {
      try {
        const result = await serverRequest('POST', 'users/invite', userData);
        return result;
      } catch (error) {
        // Re-throw the error so the mutation can handle it properly
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      // Don't re-throw here, let the component handle it
    },
    // Add timeout and retry options
    retry: 1, // Retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      return await serverRequest('PATCH', `users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      throw error;
    },
  });
};

// Update user status
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      return await serverRequest('PATCH', `users/deactivate/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      throw error;
    },
  });
};

// Delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      return await serverRequest('DELETE', `users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      notifyUser('success', 'User deleted successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};
